import { SessionHelper } from "app/helpers/sessionHelper";
import { TimeDateHelper } from "app/helpers/timeDateHelper";
import { SessionType } from "app/types/userType";
import {
  MAX_WEBSOCKET_MESSAGE_SIZE,
  PollingIntervals,
  ScreenType,
} from "app/utils/const";
import isEmpty from "lodash/isEmpty";
import ENV from "../../config";
import store from "../store/store";
import { WsHelper } from "./wsHelper";

let numberOfSocketReconnections: number = 0; //Increments each time there is a new ws created. To help with debugging potential issues.
let pingStarted: boolean = false;
let pingIntervalId: ReturnType<typeof setInterval>;
let reconnectDelay = 10000; //Wait after close before trying to reconnect
let pingCount: number = 0; // Keep track of number of pings sent without a reply ping
let socket: WebSocket | null;
let pingSentTimeStamp: number; // Not part of ws implementation, maybe could use in the future for checking network speed
let pingReceivedTimeStamp: number; // For checking network speed
let reconnectTimeout;

const setupSocket = (): any => {
  console.log("setUpSocket");

  const ping = () => {
    pingSentTimeStamp = performance.now(); // timestamp to calculate how long a ping takes to get a response, maybe use for checking network speed

    // When subscribeModel isWebsocketReady is false:
    // - Connection Error message is shown to the user,
    // - The polling frequency for betslip prices is increased, ie BETSLIP_PRICE_POLLING_FAST.
    if (pingCount === 0) {
      store.dispatch.subscribeModel.updateWebsocketState({
        isReady: true,
      });

      // pingCount++;
    } else if (pingCount === 1) {
      // means the last ping didnt get a reply, so show warning message and increase betslip price polling frequecny
      store.dispatch.subscribeModel.updateWebsocketState({
        isReady: false,
      });
    } else if (pingCount === 3) {
      // If no reply after 3 pings, ie 15 seconds, there is an issue so close ws and try to reconnect
      socket?.close(1000, "Ping not received"); // Close socket with code 1000 and a message
      store.dispatch.subscribeModel.updateWebsocketState({
        isReady: false,
      });
      store.dispatch.subscribeModel.isReconnecting({
        reconnecting: true,
      });
    }
    if (socket && socket.readyState === 1) {
      socket.send(
        JSON.stringify({
          type: "ping",
          numberOfSocketReconnections,
        })
      );
    } else {
      console.log("Ping not sent - socket not ready");
    }

    pingCount++;
  };
  const createNewSocket = () => {
    console.log("createNewSocket socket: ", socket);

    const newWebsocketCreatedAt =
      TimeDateHelper.getUTCFormattedDateTime("HH:mm:ss:SSS");
    console.log("newWebsocketCreatedAt: ", newWebsocketCreatedAt);
    socket = new WebSocket(ENV.WEBSOCKET_HOST + "?api=v2");
    addListeners();
  };
  const addListeners = () => {
    socket?.addEventListener("open", event => {
      console.log("onOpen event: ", event);
      const timeOnOpenWs =
        TimeDateHelper.getUTCFormattedDateTime("HH:mm:ss:SSS");
      const session: SessionType = store.getState().sessionModel.session;
      const wsOpenMessage = `WS onOpen: User: ${session.username} IPAddress: ${session.ipAddress} uiViewMode: ${session.uiViewMode} TimeOpened: ${timeOnOpenWs} NumberOfSocketReconnections: ${numberOfSocketReconnections}`;
      console.log("wsOpenMessage: ", wsOpenMessage);
      login();
      store.dispatch.subscribeModel.updateWebsocketState({
        isReady: true,
      });

      // if web socket connection has closed reconnect and resubscribe events, market and runners
      let resubscribeEventsMarketRunners =
        store.getState().subscribeModel.events;
      // if subscription model has stored data resubscribe events, market and runners
      !isEmpty(resubscribeEventsMarketRunners) &&
        store.dispatch.subscribeModel.resubscribeEventsMarketAndRunners();
      // onopen set isreconnecting to false
      socket?.readyState === 1 &&
        store.dispatch.subscribeModel.isReconnecting({
          reconnecting: false,
        });

      if (!pingStarted) {
        pingIntervalId = setInterval(ping, PollingIntervals.WEBSOCKETPING);
        pingStarted = true;
      }
      numberOfSocketReconnections++;
    });

    socket?.addEventListener("message", event => {
      const data = JSON.parse(event.data);
      if (data.type === "ping") {
        pingReceivedTimeStamp = performance.now();
        const timeToReceivePing = pingReceivedTimeStamp - pingSentTimeStamp;
        // console.log(
        //   "Time to receive ping: ",
        //   timeToReceivePing
        // );
        pingCount--;
      } else {
        WsHelper.onMessage(data);
      }
    });

    socket?.addEventListener("close", event => {
      console.log("on ws close:  event: ", event);
      console.log("on ws close:  socket: ", socket);
      const timeClose = TimeDateHelper.getUTCFormattedDateTime("HH:mm:ss:SSS");
      const session: SessionType = store.getState().sessionModel.session;

      const wsCloseMessage = `WS onClose: User: ${
        session.username
      } IPAddress: ${session.ipAddress} Reason: ${
        event.reason || "None"
      } Code: ${event.code || "None"}  WasClean: ${
        event.wasClean
      } Time: ${timeClose} NumberOfSocketReconnections: ${numberOfSocketReconnections}`;
      console.log("wsCloseMessage: ", wsCloseMessage);
      console.log("=================");
      socket = null;
      pingStarted = false;
      clearInterval(pingIntervalId);
      pingCount = 0;

      // Make sure only one timer is running
      clearTimeout(reconnectTimeout);

      const isUserSessionNull = event?.reason.includes(
        "Logged in user id (null) or session token (null)"
      );
      console.log("isUserSessionNull: ", isUserSessionNull);
      // Dont try to reconnect if user session is null, eg user has been logged out by the server
      if (!isUserSessionNull) {
        reconnectTimeout = setTimeout(function () {
          createNewSocket();
        }, reconnectDelay);
      }
    });

    socket?.addEventListener("error", event => {
      console.log("Web Socket Error:", event);
    });
  };
  createNewSocket();
};

export const sendSocket = (payload: any) => {
  if (isPayloadSizeUnderThreshold(payload)) {
    // Send the message
    const viewMode = SessionHelper.isAsianRouteAndSessionView()
      ? ScreenType.ASIAN
      : ScreenType.EUROPEAN;
    payload["viewMode"] = viewMode;
    if (socket?.readyState === 1) {
      socket.send(JSON.stringify(payload));
    } else {
      console.log("socket not ready, socket.readyState: ", socket?.readyState);
    }
  } else {
    console.log(
      "Message size exceeds the maximum allowed size, payload:",
      payload
    );
  }
};

// Send a login msg to WS with session-token
const login = () => {
  if (SessionHelper.isAuthenticated()) {
    const sessionToken = SessionHelper.getToken();
    sendSocket({
      type: "login",
      data: sessionToken,
    });
  }
};

const isPayloadSizeUnderThreshold = payload => {
  // If payload size is greater than the buffer size set by the server, connection will get closed.
  // So to avoid this, check the size of the message and only send it if it is under a threshold

  // The result of this code will give the total number of encoded characters in the URI-encoded JSON string.
  const messageSizeInBytes =
    encodeURI(JSON.stringify(payload)).split(/%..|./).length - 1;
  // Compare the message size with a maximum allowed size

  return messageSizeInBytes <= MAX_WEBSOCKET_MESSAGE_SIZE;
};

export default setupSocket;
